// The side panel's face while two instances are being compared: it takes Overview's slot, and the
// step inspector still docks below it, so inspecting a step works exactly as it does anywhere else.
//
// The map next to it draws the union of the pair — shared stretches as one spine, divergences as
// two coloured branches. What pooling costs is *order*: a step visited three times is one box. So
// this panel owns the two readings the map cannot give — the headline deltas, and the two runs
// aligned into a single list in the order they actually happened.
import * as Tooltip from "@radix-ui/react-tooltip";
import { ArrowLeftRight, ChevronDown, Columns2, MessageCircleQuestion } from "lucide-react";
// biome-ignore lint/style/useImportType: this bundle compiles JSX with the classic React runtime.
import React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatDuration } from "../../utils";
import type { ScreenshotRef } from "../screenshots";
import AppUsageBars from "./AppUsageBars";
import ProcessInstancePicker, { useProcessInstanceOptions } from "./ProcessInstancePicker";
import type { TransitionWaitAskContext } from "./ProcessOverviewPanel";
import ProcessOccurrenceEvidence from "./ProcessOccurrenceEvidence";
import { buildPooledAppRows } from "./processAppUsage";
import {
  type CompareAlignmentRow,
  type CompareInstanceStats,
  type CompareSide,
  alignInstanceSteps,
  compareGroupStats,
  compareInstanceStats,
  compareNodeStats,
  compareSideColor,
  compareSideInk,
  compareSideTextClass,
  formatCompareCount,
} from "./processCompareModel";
import { type ProcessInstance, type ProcessStep, stepWaitSeconds } from "./processGraphModel";
import { formatProcessSourceStartTime } from "./processInstanceLabels";
import {
  CollapsibleSidebarSection,
  HelpHint,
  PanelSection,
  ShowAllButton,
  StepAppIcon,
  panelScrollBehavior,
} from "./processSidebarUi";
import {
  StepKeysHint,
  WHEEL_ROW_HEIGHT,
  WaitGapMarker,
  WheelStack,
  useStepKeysHint,
  useStepWheel,
  useStepWheelKeys,
} from "./processStepWheel";
import type { ProcessGraphState } from "./useProcessGraph";

/**
 * Which run the wheel is currently reading, and where in that run the focused step falls. The map
 * needs both: the side picks whose route flows and in which colour, the position labels the box.
 */
export type CompareStepFocus = {
  side: CompareSide;
  /** 1-based position of the step within that run. */
  current: number;
  /** How many steps that run has in total. */
  total: number;
};

/** The A/B letter, as a filled square in the side's ink — the map's key, in text. The mark is the
 *  one place each side is stated at full strength: it is 16px square, so it can be solid without
 *  weighing anything, and it is what lets every larger surface stay a tint. */
const SideMark = ({
  side,
  className = "",
}: {
  side: CompareSide;
  className?: string;
}) => (
  <span
    aria-hidden="true"
    className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] text-[10px] font-bold leading-none ${className}`}
    style={{ backgroundColor: compareSideInk(side), color: "#fff" }}
  >
    {side === "a" ? "A" : "B"}
  </span>
);

/** Run numbers restart in each recording; compare identifies runs by who and when. */
const useCompareIdentity = (instance: ProcessInstance | null) => {
  const { t } = useTranslation();
  return useMemo(() => {
    if (!instance) return null;
    const name = instance.source.userName?.trim() || t("user");
    const startTime = formatProcessSourceStartTime(instance.source);
    return { name, startTime, label: `${name} · ${startTime}` };
  }, [instance, t]);
};

const useComparedInstances = (state: ProcessGraphState) => {
  const { comparePair, processInstances } = state;
  return useMemo(() => {
    if (!comparePair) return null;
    const a = processInstances.find(instance => instance.id === comparePair.aId) ?? null;
    const b = processInstances.find(instance => instance.id === comparePair.bId) ?? null;
    return a && b ? { a, b } : null;
  }, [comparePair, processInstances]);
};

/** One side's picker: its mark, its name, and the menu that re-aims it.
 *  `compact` is the page-header face — one line instead of two, so it fits a toolbar row. */
const CompareSideRow = ({
  state,
  side,
  instance,
  compact = false,
}: {
  state: ProcessGraphState;
  side: CompareSide;
  instance: ProcessInstance;
  compact?: boolean;
}) => {
  const { t } = useTranslation();
  const { process, processInstances, setCompareInstance } = state;
  const allInstanceIds = useMemo(
    () => new Set(processInstances.map(candidate => candidate.id)),
    [processInstances],
  );
  const options = useProcessInstanceOptions(process, processInstances, allInstanceIds);
  const identity = useCompareIdentity(instance);

  return (
    <ProcessInstancePicker
      process={process}
      options={options}
      selectedInstanceId={instance.id}
      onSelect={instanceId => setCompareInstance(side, instanceId)}
      align="start"
      label={t("compare_pick_side")}
    >
      <button
        type="button"
        title={identity?.label ?? t("compare_pick_side")}
        className={
          compact
            ? // Tinted in the side's own colour rather than flooded with it: in the header the pair
              // *is* the map's key, and the solid A/B mark plus a wash of the same hue says whose
              // colour is whose while the pill still sits in the toolbar as one more control.
              "flex min-h-8 min-w-0 max-w-[16rem] items-center gap-1.5 rounded-full border border-outline px-3 py-1 text-left text-text outline-none transition hover:brightness-[0.97] focus-visible:ring-2 focus-visible:ring-primary/30"
            : "flex w-full min-w-0 items-center gap-2 rounded-lg border border-outline bg-surface px-2 py-1.5 text-left outline-none transition hover:bg-surface-strong focus-visible:ring-2 focus-visible:ring-primary/30"
        }
        style={
          compact
            ? {
                backgroundColor: `color-mix(in srgb, ${compareSideColor(side)} 18%, var(--color-surface))`,
                borderColor: `color-mix(in srgb, ${compareSideColor(side)} 45%, transparent)`,
              }
            : { borderLeft: `3px solid ${compareSideColor(side)}` }
        }
      >
        <SideMark side={side} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs font-medium text-text">{identity?.name}</span>
          <span className="block whitespace-nowrap text-[10px] text-text-mid">
            {identity?.startTime}
          </span>
        </span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-text-dim" aria-hidden="true" />
      </button>
    </ProcessInstancePicker>
  );
};

type CompareMetric = {
  key: string;
  label: string;
  /** Both readings, already rendered; null where the analysis cannot answer. */
  a: string;
  b: string;
  /** b − a in the metric's own unit, or null when there is nothing to subtract. */
  delta: number | null;
  format: (value: number) => string;
  /** Shown behind a "?" next to the label, for the readings whose definition is not obvious. */
  help?: string;
  /** Accessible name for that "?" trigger. */
  helpLabel?: string;
};

/** Everything the chat needs to be handed one row of the table as a question. */
export type CompareMetricAskContext = {
  metricKey: string;
  metricLabel: string;
  aLabel: string;
  bLabel: string;
  aValue: string;
  bValue: string;
  /** The signed difference, B minus A, as the table prints it. */
  delta: string;
};

/** The delta as text — the same reading the column shows — or null when there is none to show. */
const formatMetricDelta = (metric: CompareMetric): string | null => {
  const { delta, format } = metric;
  if (delta === null || delta === 0) return null;
  return `${delta > 0 ? "+" : "−"}${format(Math.abs(delta))}`;
};

const buildMetrics = (
  a: CompareInstanceStats,
  b: CompareInstanceStats,
  t: (key: string) => string,
  groups = false,
): CompareMetric[] => {
  const count = formatCompareCount;
  const missing = "—";
  const duration = (value: number) => formatDuration(value);
  return [
    {
      key: "total",
      label: t("processPanel.metricWorkTime"),
      a: duration(a.totalSeconds),
      b: duration(b.totalSeconds),
      delta: b.totalSeconds - a.totalSeconds,
      format: duration,
      help: t("compare_total_duration_help_text"),
      helpLabel: t("compare_total_duration_help"),
    },
    {
      key: "elapsed",
      label: t("processPanel.metricStartToFinish"),
      a: a.elapsedSeconds === null ? missing : duration(a.elapsedSeconds),
      b: b.elapsedSeconds === null ? missing : duration(b.elapsedSeconds),
      delta:
        a.elapsedSeconds === null || b.elapsedSeconds === null
          ? null
          : b.elapsedSeconds - a.elapsedSeconds,
      format: duration,
      help: t("processPage.comparisonElapsedHelp"),
      helpLabel: t("processPanel.metricStartToFinish"),
    },
    {
      key: "waiting",
      label: t("compare_waiting_time"),
      a: a.waitingSeconds === null ? missing : duration(a.waitingSeconds),
      b: b.waitingSeconds === null ? missing : duration(b.waitingSeconds),
      // Null on either side is "we cannot tell", which is not a delta of zero.
      delta:
        a.waitingSeconds === null || b.waitingSeconds === null
          ? null
          : b.waitingSeconds - a.waitingSeconds,
      format: duration,
      help: t("compare_waiting_time_help_text"),
      helpLabel: t("compare_waiting_time_help"),
    },
    {
      key: "steps",
      label: t(groups ? "processPage.averageSteps" : "compare_steps"),
      a: count(a.stepCount),
      b: count(b.stepCount),
      delta: b.stepCount - a.stepCount,
      format: count,
    },
    {
      key: "rework",
      label: t(groups ? "processPage.averageRepeats" : "compare_rework"),
      a: count(a.reworkCount),
      b: count(b.reworkCount),
      delta: b.reworkCount - a.reworkCount,
      format: count,
    },
  ];
};

/**
 * The column reads B minus A. Its sign names the direction without relying on colour;
 * equal readings are zero, while unavailable readings remain unknown.
 */
const CompareDelta = ({ metric }: { metric: CompareMetric }) => {
  const delta = formatMetricDelta(metric);
  // Nothing to explain: the two runs read alike, or the analysis cannot say.
  if (metric.delta === null) return <span className="text-text-dim">—</span>;
  if (metric.delta === 0) return <span className="text-text-mid">{metric.format(0)}</span>;
  return (
    <span
      className={`whitespace-nowrap font-medium tabular-nums ${compareSideTextClass(metric.delta > 0 ? "b" : "a")}`}
    >
      {delta}
    </span>
  );
};

/**
 * The row's question, named in full. It rests in the gutter between the metric and its readings
 * and surfaces on hover, so every row carries the offer without four buttons competing for the
 * eye at rest.
 */
const CompareAskWhy = ({
  metric,
  aLabel,
  bLabel,
  onAsk,
}: {
  metric: CompareMetric;
  aLabel: string;
  bLabel: string;
  onAsk: (ask: CompareMetricAskContext) => void;
}) => {
  const { t } = useTranslation();
  const delta = formatMetricDelta(metric);
  if (delta === null || metric.delta === null) return null;
  return (
    <button
      type="button"
      onClick={() =>
        onAsk({
          metricKey: metric.key,
          metricLabel: metric.label,
          aLabel,
          bLabel,
          aValue: metric.a,
          bValue: metric.b,
          delta,
        })
      }
      aria-label={t("ask_why_compare_metric", { metric: metric.label, a: aLabel, b: bLabel })}
      title={t("ask_why_compare_metric", { metric: metric.label, a: aLabel, b: bLabel })}
      className="absolute right-1 top-1/2 inline-flex -translate-y-1/2 items-center gap-1 whitespace-nowrap rounded-md bg-surface-strong px-2 py-1 text-xs font-medium text-text shadow-sm opacity-0 transition focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-primary/30 group-hover:opacity-100 group-focus-within:opacity-100"
    >
      {t("ask_why")}
      <MessageCircleQuestion aria-hidden="true" className="h-3 w-3 shrink-0" />
    </button>
  );
};

const CompareMetricsTable = ({
  metrics,
  aLabel,
  bLabel,
  onAsk,
}: {
  metrics: CompareMetric[];
  aLabel: string;
  bLabel: string;
  onAsk: (ask: CompareMetricAskContext) => void;
}) => {
  const { t } = useTranslation();
  return (
    <Tooltip.Provider delayDuration={200}>
      <table className="w-full table-fixed border-collapse text-sm">
        <colgroup>
          <col className="w-[40%]" />
          <col className="w-[20%]" />
          <col className="w-[20%]" />
          <col className="w-[20%]" />
        </colgroup>
        <thead>
          <tr className="border-b border-outline text-[10px] uppercase tracking-wide text-text-dim">
            <th scope="col" className="py-1.5 text-left font-semibold">
              {t("compare_metric")}
            </th>
            <th scope="col" className="py-1.5 pr-2 text-left font-semibold">
              <span className="inline-flex items-center gap-1">
                <SideMark side="a" />
                <span className="sr-only">A: {aLabel}</span>
              </span>
            </th>
            <th scope="col" className="py-1.5 pr-2 text-left font-semibold">
              <span className="inline-flex items-center gap-1">
                <SideMark side="b" />
                <span className="sr-only">B: {bLabel}</span>
              </span>
            </th>
            <th scope="col" className="py-1.5 text-left font-semibold">
              <span title={t("processPage.comparisonDeltaHelp")} className="normal-case">
                {t("processPage.comparisonDelta")}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {metrics.map(metric => (
            <tr key={metric.key} className="group border-b border-outline/60 last:border-b-0">
              <th scope="row" className="relative py-2.5 pr-3 text-left font-normal text-text-mid">
                <span className="flex min-w-0 flex-wrap items-center gap-1">
                  <span>{metric.label}</span>
                  {metric.help && (
                    <HelpHint label={metric.helpLabel ?? metric.label} text={metric.help} />
                  )}
                </span>
                <CompareAskWhy metric={metric} aLabel={aLabel} bLabel={bLabel} onAsk={onAsk} />
              </th>
              <td className="py-2.5 pr-2 text-left tabular-nums text-text">{metric.a}</td>
              <td className="py-2.5 pr-2 text-left tabular-nums text-text">{metric.b}</td>
              <td className="py-2.5 text-left tabular-nums">
                <CompareDelta metric={metric} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Tooltip.Provider>
  );
};

/** One side's reading on a row: where in that run the step falls, and what it spent there. */
type CompareSideCell = { index: number; step: ProcessStep } | null;

/** What a row amounts to on one side, or null where that run never takes the step. */
const sideCell = (
  row: CompareAlignmentRow,
  side: CompareSide,
  steps: { a: ProcessStep[]; b: ProcessStep[] },
): CompareSideCell => {
  const index =
    row.kind === "shared"
      ? side === "a"
        ? row.aIndex
        : row.bIndex
      : row.side === side
        ? row.index
        : null;
  if (index === null) return null;
  const step = steps[side][index];
  return step ? { index, step } : null;
};

/** Which side spent the longer on a step both runs took, or null when there is nothing between them. */
const slowerSide = (a: CompareSideCell, b: CompareSideCell): CompareSide | null => {
  if (!a || !b || a.step.duration === b.step.duration) return null;
  return a.step.duration > b.step.duration ? "a" : "b";
};

/**
 * One column of one row. A run's steps live in its own column and nowhere else, so which run a
 * reading belongs to is answered by where it sits: the side needs no badge, and a step only one
 * run took needs no dot standing in for the other.
 *
 * That absence is the column's real work. The facing cell goes blank and recessed, which is the
 * whole of the divergence signal — an empty half-row is visible from further away than any mark
 * could be, and it costs nothing to read. Where both runs did take the step, colour is spent on
 * the longer of the two durations instead, so a scan down the list lands on the time lost rather
 * than on the fact that there are two runs.
 */
const CompareStepCell = ({
  side,
  cell,
  name,
  app,
  total,
  label,
  slower,
  strong = false,
  dim = false,
  active = false,
  open,
  onToggle,
  markers,
}: {
  side: CompareSide;
  cell: CompareSideCell;
  name: string;
  app: string | null | undefined;
  /** How many steps this run has, for the position the cell's number stands for. */
  total: number;
  /** This run's name, for the title behind that position. */
  label: string;
  /** True where both runs took the step and this one spent the longer on it. */
  slower: boolean;
  /** The focused row, whose name is allowed to wrap rather than truncate. */
  strong?: boolean;
  /** Buffered rows render inert, so they carry no titles either. */
  dim?: boolean;
  /** The focused row's live column — the one Enter opens and the left/right keys move between. */
  active?: boolean;
  /** Whether this side's detail is showing. Only the focused row offers the disclosure. */
  open?: boolean;
  /** Given by the focused row: shows or hides *this run's* reading of the step. */
  onToggle?: () => void;
  /** Wait pills for this run's pauses around the step — they straddle the cell's own edges. */
  markers?: React.ReactNode;
}) => {
  const { t } = useTranslation();
  // The rule between the two tracks. It runs the whole length of the list, which is what makes
  // the columns read as two runs rather than as one table that happens to be wide.
  const divider = side === "b" ? "border-l border-outline/70" : "";

  if (!cell) {
    return (
      <div
        className={`flex h-full min-w-0 items-center justify-center px-2 ${divider}`}
        // Carried in its column's own hue, at the same strength as a filled cell: the track stays
        // one flat colour the whole way down, so the only thing that stands out of a column is the
        // pick. What marks a skipped step is the words in it, not a shade of its own.
        style={{
          backgroundColor: `color-mix(in srgb, ${compareSideColor(side)} 6%, transparent)`,
        }}
      >
        {/* Said in words rather than left as a gap: a blank half-row is unmistakable once you know
            the columns, and a dead giveaway of nothing at all until you do. Small and dim, so a
            run of detours reads as texture down the column instead of as a stack of sentences. */}
        <span className="truncate text-[10px] leading-tight text-text-dim/80">
          {t("compare_step_skipped")}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`relative flex h-full min-w-0 flex-col justify-center gap-0.5 px-2 py-1.5 ${
        onToggle ? "pr-7" : ""
      } ${divider} ${active ? "ring-1 ring-inset ring-text/70" : ""}`}
      style={{
        // Every reading is tinted in the column's own hue, at one flat strength: which run a row
        // belongs to is legible from the colour alone, and nothing else in the column competes
        // with the pick. The live column is that same tint pulled harder, so the pick reads as a
        // deepening of the column it is in rather than as a fourth colour laid over it.
        backgroundColor: `color-mix(in srgb, ${compareSideColor(side)} ${active ? 20 : 6}%, transparent)`,
      }}
    >
      {markers}
      <span className="flex min-w-0 items-center gap-1.5">
        <StepAppIcon app={app} fallback="space" />
        <span
          title={dim || strong ? undefined : name}
          className={`min-w-0 flex-1 leading-tight text-text ${
            strong ? "line-clamp-2 text-xs font-medium" : "truncate text-[11px]"
          }`}
        >
          {name}
        </span>
      </span>
      {/* Indented to the name's own left edge: the number and the duration are that step's
          footnote, not a second column of their own. */}
      <span className="flex items-center gap-1 pl-[22px] text-[10px] leading-tight tabular-nums text-text-dim">
        {/* The position is the one number that says where in its own run this step sits, so it
            gets a badge of its own rather than sharing a line of grey footnote text. */}
        <span
          title={
            dim
              ? undefined
              : `${label} · ${t("step_position_long", { current: cell.index + 1, total })}`
          }
          className="inline-flex h-[15px] min-w-[15px] items-center justify-center rounded-full px-[3px] text-[9px] font-semibold text-text"
          style={{
            backgroundColor: `color-mix(in srgb, ${compareSideColor(side)} 26%, transparent)`,
          }}
        >
          {cell.index + 1}
        </span>
        <span className={slower ? `font-semibold ${compareSideTextClass(side)}` : undefined}>
          {formatDuration(cell.step.duration)}
        </span>
      </span>
      {/* One disclosure per column, because what it opens belongs to one run: this side's own
          description, objects and screenshots for its own occurrence of the step. A column with
          nothing here has nothing to open, and shows no control at all. */}
      {onToggle && (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-label={t(open ? "compare_hide_step_detail" : "compare_show_step_detail", { label })}
          title={t(open ? "compare_hide_step_detail" : "compare_show_step_detail", { label })}
          className="absolute right-1 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-text-dim outline-none transition hover:bg-text/[0.06] hover:text-text focus-visible:ring-2 focus-visible:ring-primary/30"
        >
          <ChevronDown
            aria-hidden="true"
            className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      )}
    </div>
  );
};

/**
 * Which column belongs to which run, said once above the list rather than repeated on every row.
 * The columns are fixed for the whole path, so naming them here is enough — and it hands every
 * row below its full width back for the step's name.
 */
const CompareColumnHeader = ({
  labels,
  identities,
  totals,
}: {
  labels: { a: string; b: string };
  identities: {
    a: ReturnType<typeof useCompareIdentity>;
    b: ReturnType<typeof useCompareIdentity>;
  };
  totals: { a: number; b: number };
}) => {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 border-b border-outline">
      {(["a", "b"] as const).map(side => (
        <div
          key={side}
          className={`flex min-w-0 items-center gap-1.5 px-2 py-1.5 ${
            side === "b" ? "border-l border-outline" : ""
          }`}
          style={{
            backgroundColor: `color-mix(in srgb, ${compareSideColor(side)} 12%, transparent)`,
          }}
        >
          <SideMark side={side} />
          <span title={labels[side]} className="min-w-0 flex-1 text-[11px] font-medium text-text">
            <span className="block truncate">{identities[side]?.name}</span>
            <span className="block text-[10px] font-normal text-text-mid">
              {identities[side]?.startTime}
            </span>
          </span>
          {/* How long this run is, said once here — which is what lets every row below carry a
              bare step number instead of "n of m" twice over. */}
          <span className="shrink-0 text-[10px] tabular-nums text-text-dim">
            {t("n_steps", { count: totals[side] })}
          </span>
        </div>
      ))}
    </div>
  );
};

/**
 * The two runs as one wheel, in the order they ran: the focused row opens into a card that stays
 * put while the rest of the combined path travels past it, exactly as one instance's own steps
 * do. Shared rows carry both readings; a step only one side took is tinted in that side's colour,
 * which is what makes a divergence read the way a diff does.
 *
 * Focus and the map's selection are the same pick: turning the wheel selects and recentres the
 * step on the map, and picking a box there turns the wheel to it. Nothing docks below this list
 * while a pair is compared, so the two can be tied without the panel moving under the wheel.
 */
const CompareStepWheel = ({
  state,
  instances,
  rows,
  onFocusStep,
  onReachBoundary,
  onShowScreenshots,
  onAskWaitingTime,
  stepKeysActive,
  showStepKeysHint,
  onStepKeysHintShown,
}: {
  state: ProcessGraphState;
  instances: { a: ProcessInstance; b: ProcessInstance };
  /** The two runs' aligned steps, computed once by the panel (its header counts the same rows). */
  rows: CompareAlignmentRow[];
  onFocusStep: (nodeId: string, focus?: CompareStepFocus) => void;
  onReachBoundary: (nodeId: string, kind: "start" | "end") => void;
  onShowScreenshots: (screenshots: ScreenshotRef[]) => void;
  onAskWaitingTime: (transition: TransitionWaitAskContext) => void;
  stepKeysActive: boolean;
  showStepKeysHint: boolean;
  onStepKeysHintShown: () => void;
}) => {
  const { t } = useTranslation();
  const { process } = state;
  const nodeById = useMemo(
    () => new Map(process.nodes.map(node => [node.id, node])),
    [process.nodes],
  );
  const aIdentity = useCompareIdentity(instances.a);
  const bIdentity = useCompareIdentity(instances.b);
  const labels = {
    a: aIdentity?.label ?? instances.a.id,
    b: bIdentity?.label ?? instances.b.id,
  };
  const totals = useMemo(
    () => ({ a: instances.a.steps.length, b: instances.b.steps.length }),
    [instances.a.steps.length, instances.b.steps.length],
  );

  // A step can appear at several points of the combined path, so a node id alone doesn't name a
  // row: the one nearest where the wheel already rests is the one the user means.
  const rowForNode = useCallback(
    (nodeId: string, near: number) => {
      let best = -1;
      rows.forEach((row, index) => {
        if (row.nodeId !== nodeId) return;
        if (best < 0 || Math.abs(index - near) < Math.abs(best - near)) best = index;
      });
      return best;
    },
    [rows],
  );

  const selectedNodeId = state.selection?.kind === "node" ? state.selection.id : null;
  // The map's selection is where the wheel starts, so arriving from a clicked box — or from the
  // step compare was launched on — opens the list on that step rather than at the top.
  const [focusRow, setFocusRow] = useState(() =>
    selectedNodeId ? Math.max(rowForNode(selectedNodeId, 0), 0) : 0,
  );
  const focusIndex = Math.min(Math.max(focusRow, 0), Math.max(rows.length - 1, 0));
  const wheel = useStepWheel(focusIndex, rows.length);

  // The other direction: a box picked on the map turns the wheel to it, so a click there reads
  // the same as a click here.
  useEffect(() => {
    if (!selectedNodeId) return;
    setFocusRow(current => {
      const bounded = Math.min(Math.max(current, 0), Math.max(rows.length - 1, 0));
      if (rows[bounded]?.nodeId === selectedNodeId) return current;
      const next = rowForNode(selectedNodeId, bounded);
      return next < 0 ? current : next;
    });
  }, [rowForNode, rows, selectedNodeId]);

  // The focused row says the same four things every other row says — logo, name, position, time.
  // Everything the step *is* — its description, its objects, its screenshots — is a second reading
  // the list does not owe you, and it is owed *per side*: the two runs met the same step at
  // different points, with their own screenshots and their own objects, so each column opens its
  // own. Opened once it stays open as the wheel travels, which makes it a mode the user turns on
  // rather than a card that keeps reappearing under every step they scroll past.
  const [detailsOpen, setDetailsOpen] = useState<Record<CompareSide, boolean>>({
    a: false,
    b: false,
  });
  const toggleDetails = useCallback(
    (side: CompareSide) => setDetailsOpen(open => ({ ...open, [side]: !open[side] })),
    [],
  );

  // Opening a column's card grows the focused row downwards, and near the panel's bottom edge that
  // growth lands below the fold: the card the user just asked for is the part that gets cut. So the
  // panel is pulled after it. Keyed on how many cards are open, not on whether any is, so opening
  // the second column scrolls too — the first one leaving `open` true would otherwise swallow it.
  const openDetailCount = Number(detailsOpen.a) + Number(detailsOpen.b);
  const sectionRef = useRef<HTMLElement | null>(null);
  const openDetailCountRef = useRef(openDetailCount);
  useEffect(() => {
    const grew = openDetailCount > openDetailCountRef.current;
    openDetailCountRef.current = openDetailCount;
    if (!grew) return;
    const element = sectionRef.current;
    if (!element) return;
    // The card's own contents (objects, screenshots) settle a frame after paint; measuring before
    // then under-scrolls and leaves the tail cut off anyway.
    const frame = window.requestAnimationFrame(() =>
      // The whole section is brought to rest against the bottom of the panel — the card and the
      // rows below it alike, so nothing of the step-by-step is left under the fold.
      element.scrollIntoView?.({ behavior: panelScrollBehavior(), block: "end" }),
    );
    return () => window.cancelAnimationFrame(frame);
  }, [openDetailCount]);

  const stepLists = useMemo(
    () => ({ a: instances.a.steps, b: instances.b.steps }),
    [instances.a.steps, instances.b.steps],
  );

  // A pause belongs to one run, so it is a reading of one column — never of the row, which is two
  // runs at once. Each column keeps its own table, read exactly as the single-instance wheel
  // reads its one.
  const waitAfter = useMemo(
    () => ({ a: stepWaitSeconds(instances.a.steps), b: stepWaitSeconds(instances.b.steps) }),
    [instances.a.steps, instances.b.steps],
  );

  /** The pause after this run's step `index`, drawn on that cell's own border. */
  const renderWaitMarker = (side: CompareSide, index: number, edge: "top" | "bottom") => {
    const seconds = waitAfter[side][index];
    const steps = stepLists[side];
    const from = steps[index];
    const to = steps[index + 1];
    if (seconds === null || seconds === undefined || !from || !to) return null;
    const stepIdentity = (step: ProcessStep) => {
      const node = nodeById.get(step.nodeId);
      return { id: step.nodeId, name: node?.name ?? step.nodeId, app: node?.app };
    };
    return (
      <WaitGapMarker
        seconds={seconds}
        from={stepIdentity(from)}
        to={stepIdentity(to)}
        edge={edge}
        onAsk={onAskWaitingTime}
        className="px-2"
      />
    );
  };

  /** Both sides of a row, and which of them — where both took the step — spent the longer. */
  const rowCells = useCallback(
    (row: CompareAlignmentRow) => {
      const a = sideCell(row, "a", stepLists);
      const b = sideCell(row, "b", stepLists);
      return { a, b, slower: slowerSide(a, b) };
    },
    [stepLists],
  );

  // Up and down pick the step; left and right pick which run's reading of it is live. That second
  // axis is what makes Enter unambiguous on a shared row: it opens the column the keys are on.
  const [activeSide, setActiveSide] = useState<CompareSide>("a");
  const focusedCells = rows[focusIndex] ? rowCells(rows[focusIndex]) : null;
  const liveSide: CompareSide =
    focusedCells && !focusedCells[activeSide] && focusedCells[activeSide === "a" ? "b" : "a"]
      ? activeSide === "a"
        ? "b"
        : "a"
      : activeSide;
  // A row only one run took has one column to be on, so travelling onto it moves the keys there
  // rather than leaving them parked on a blank half-row.
  useEffect(() => {
    if (liveSide !== activeSide) setActiveSide(liveSide);
  }, [activeSide, liveSide]);

  // Each case keeps its own reading position when the other case took a different path.
  const lastRowBySide = useRef<Partial<Record<CompareSide, number>>>({});
  useEffect(() => {
    if (focusedCells?.[liveSide]) lastRowBySide.current[liveSide] = focusIndex;
  }, [focusIndex, focusedCells, liveSide]);

  // Which run the map should follow for a row, and where the step falls in it. `preferred` is the
  // column the keys are on; a row that run did not take has only the other side to read.
  const focusForRow = useCallback(
    (index: number, preferred: CompareSide): CompareStepFocus | undefined => {
      const row = rows[index];
      if (!row) return undefined;
      const other: CompareSide = preferred === "a" ? "b" : "a";
      const side = sideCell(row, preferred, stepLists) ? preferred : other;
      const cell = sideCell(row, side, stepLists);
      if (!cell) return undefined;
      return { side, current: cell.index + 1, total: totals[side] };
    },
    [rows, stepLists, totals],
  );

  // Moving the wheel takes the map with it: the combined path is drawn there, and travelling a
  // list of step names is only half the reading if the box it names stays off screen. The map
  // selects that step too, so the wheel's focused card and the lit box are one pick — and it
  // follows the run the focused column belongs to, so the route flowing on the map is that run's.
  const goToRow = useCallback(
    (index: number) => {
      const row = rows[index];
      if (!row) return;
      setFocusRow(index);
      onFocusStep(row.nodeId, focusForRow(index, activeSide));
    },
    [activeSide, focusForRow, onFocusStep, rows],
  );

  // The list opens already resting on a row, so the map is told about that one too — without it
  // the first route would only appear once the wheel is turned.
  const announcedRef = useRef(false);
  useEffect(() => {
    if (announcedRef.current) return;
    announcedRef.current = true;
    const row = rows[focusIndex];
    if (row) onFocusStep(row.nodeId, focusForRow(focusIndex, activeSide));
  }, [activeSide, focusForRow, focusIndex, onFocusStep, rows]);

  const { phase: keysHintPhase, dismiss: dismissKeysHint } = useStepKeysHint({
    owed: showStepKeysHint,
    eligible: stepKeysActive && rows.length > 1,
    onShown: onStepKeysHintShown,
  });

  useStepWheelKeys({
    // A step inspector opened in instance mode brings its own wheel, and that one owns the keys.
    enabled: stepKeysActive,
    onMove: useCallback(
      (delta: number) => {
        dismissKeysHint();
        // Up and down travel one run, not the combined path: the keys stay in the column they are
        // in and skip the rows that run never took, so a step the facing run took alone doesn't
        // drag the pick across to the other side. Only where the live column has nothing left
        // ahead does the move fall back to the next row of the path, whichever run it belongs to.
        for (let index = focusIndex + delta; index >= 0 && index < rows.length; index += delta) {
          const row = rows[index];
          if (!row || !sideCell(row, liveSide, stepLists)) continue;
          goToRow(index);
          return;
        }
        // Past either end of the combined path the wheel stays put, so the map labels the end it
        // is resting on rather than letting the keystroke go unanswered.
        const adjacent = focusIndex + delta;
        if (adjacent < 0 || adjacent >= rows.length) {
          const row = rows[focusIndex];
          if (row) onReachBoundary(row.nodeId, delta < 0 ? "start" : "end");
          return;
        }
        goToRow(adjacent);
      },
      [dismissKeysHint, focusIndex, goToRow, liveSide, onReachBoundary, rows, stepLists],
    ),
    onMoveAcross: useCallback(
      (delta: number) => {
        dismissKeysHint();
        const next: CompareSide = delta > 0 ? "b" : "a";
        if (next === liveSide) return;
        // Shared steps cross in place. On separate paths, resume the other case where it
        // was left, or start at its first actual step rather than selecting an empty cell.
        const nextIndex = focusedCells?.[next]
          ? focusIndex
          : (lastRowBySide.current[next] ?? rows.findIndex(row => sideCell(row, next, stepLists)));
        const row = rows[nextIndex];
        if (!row) return;
        lastRowBySide.current[liveSide] = focusIndex;
        lastRowBySide.current[next] = nextIndex;
        setActiveSide(next);
        setFocusRow(nextIndex);
        // Crossing changes whose run is being read, so the map changes route and colour with it.
        onFocusStep(row.nodeId, focusForRow(nextIndex, next));
        // The card below reads the live column, so crossing takes an open card with it: leaving it
        // on the side the keys just left would show one run's step under the other run's box.
        setDetailsOpen(open =>
          open[liveSide] && !open[next] ? { ...open, [liveSide]: false, [next]: true } : open,
        );
      },
      [
        dismissKeysHint,
        focusForRow,
        focusIndex,
        focusedCells,
        liveSide,
        onFocusStep,
        rows,
        stepLists,
      ],
    ),
    onToggleDetail: useCallback(() => {
      dismissKeysHint();
      if (!focusedCells?.[liveSide]) return;
      toggleDetails(liveSide);
    }, [dismissKeysHint, focusedCells, liveSide, toggleDetails]),
  });

  /** One slot of a neighbour stack — a collapsed row, or a spacer past either end of the path. */
  const renderSlot = (index: number, side: "above" | "below") => {
    const row = rows[index];
    const divider = `${side === "above" ? "border-b" : "border-t"} border-outline/70`;
    if (!row) {
      return (
        <li
          key={`${side}:empty:${index}`}
          aria-hidden="true"
          style={{ height: WHEEL_ROW_HEIGHT }}
        />
      );
    }
    const node = nodeById.get(row.nodeId);
    const name = node?.name ?? row.nodeId;
    const buffered = wheel.isBuffered(index, side);
    const cells = rowCells(row);
    // A pill straddles the edge it sits on, so it only renders where both halves are inside the
    // clip: the edges touching the focused card are drawn by the card, and the buffered rows' ones
    // fall on the clip edge.
    const marksWait =
      side === "above"
        ? index >= focusIndex - wheel.aboveRows && index <= focusIndex - 2
        : index >= focusIndex + 1 && index <= focusIndex + wheel.belowRows - 1;
    const body = (["a", "b"] as const).map(cellSide => {
      const cell = cells[cellSide];
      return (
        <CompareStepCell
          key={cellSide}
          side={cellSide}
          cell={cell}
          name={name}
          app={node?.app}
          total={totals[cellSide]}
          label={labels[cellSide]}
          slower={cells.slower === cellSide}
          dim={buffered}
          markers={marksWait && cell ? renderWaitMarker(cellSide, cell.index, "bottom") : undefined}
        />
      );
    });

    return (
      <li
        key={`${side}:${row.nodeId}:${index}`}
        aria-hidden={buffered || undefined}
        className={`relative box-border ${divider}`}
        style={{ height: WHEEL_ROW_HEIGHT }}
      >
        {buffered ? (
          <div className="grid h-full w-full grid-cols-2">{body}</div>
        ) : (
          // `data-wheel-row` hands Enter back to the wheel: clicking a row leaves the focus on
          // its button, and Enter there must open the step rather than re-pick the same row.
          <button
            type="button"
            onClick={() => goToRow(index)}
            title={t("compare_focus_step", { step: name })}
            data-wheel-row=""
            className="group relative grid h-full w-full grid-cols-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/35"
          >
            {body}
            {/* The hover wash goes over the cells rather than under them: each column paints its
                own background, so a background on the button would only show through the neutral
                half of the row. */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 bg-text/[0.05] opacity-0 transition-opacity group-hover:opacity-100"
            />
          </button>
        )}
      </li>
    );
  };

  const focusRowData = rows[focusIndex];
  if (!focusRowData) return null;
  const focusNode = nodeById.get(focusRowData.nodeId);
  const focusName = focusNode?.name ?? focusRowData.nodeId;
  const focusCells = focusedCells ?? rowCells(focusRowData);
  const focusA = focusCells.a;
  const focusB = focusCells.b;
  const durationDelta =
    focusA?.step && focusB?.step ? focusB.step.duration - focusA.step.duration : null;

  return (
    <>
      <section
        ref={sectionRef}
        aria-label={t("compare_step_flow")}
        className="relative overflow-hidden rounded-xl border border-outline bg-surface shadow-sm"
      >
        <CompareColumnHeader
          labels={labels}
          identities={{ a: aIdentity, b: bIdentity }}
          totals={totals}
        />

        <WheelStack wheel={wheel} side="above" renderSlot={renderSlot} />

        <div className="relative bg-focus-band">
          {/* The focused row keeps the two columns of the rows above and below it, so it reads as the
              row it is rather than as a different thing that happens to sit in the middle. Only the
              name is allowed to wrap here: this is the one row where reading it in full is the
              point, and each column carries its own disclosure. */}
          <div
            // Only the live column carries the "you are here" mark, so the pick is one cell and not
            // a whole row: the facing run's reading sits alongside it, unmarked.
            className="relative grid w-full grid-cols-2"
            style={{ minHeight: WHEEL_ROW_HEIGHT }}
            aria-current="step"
          >
            {(["a", "b"] as const).map(side => {
              const cell = focusCells[side];
              // The pause into the focused step is drawn here rather than by the row above, whose
              // bottom edge falls on the clip. Only where that row is this run's own previous
              // step: otherwise the gap already belongs to a pill further up the column.
              const previousRow = rows[focusIndex - 1];
              const previousCell = previousRow ? sideCell(previousRow, side, stepLists) : null;
              return (
                <CompareStepCell
                  key={side}
                  side={side}
                  cell={cell}
                  name={focusName}
                  app={focusNode?.app}
                  total={totals[side]}
                  label={labels[side]}
                  slower={focusCells.slower === side}
                  strong
                  active={side === liveSide && Boolean(cell)}
                  open={detailsOpen[side]}
                  onToggle={cell ? () => toggleDetails(side) : undefined}
                  markers={
                    cell && (
                      <>
                        {previousCell &&
                          previousCell.index === cell.index - 1 &&
                          renderWaitMarker(side, previousCell.index, "top")}
                        {renderWaitMarker(side, cell.index, "bottom")}
                      </>
                    )
                  }
                />
              );
            })}
          </div>

          {/* One panel per opened column, in the columns' own order. Each is that run's occurrence of
              the step — its own description, objects and screenshots — rather than a single reading
              that has to pick a side. */}
          {(["a", "b"] as const).map(side => {
            const cell = focusCells[side];
            if (!detailsOpen[side] || !cell) return null;
            const instance = side === "a" ? instances.a : instances.b;
            return (
              <div key={side} className="space-y-1 px-3 pb-3">
                <div className="space-y-3 rounded-xl border border-outline bg-background-alt p-3">
                  <ProcessOccurrenceEvidence
                    description={cell.step.description}
                    metadata={formatDuration(cell.step.duration)}
                    objects={cell.step.objects}
                    screenshots={cell.step.screenshots.map(filename => ({
                      recordingId: instance.source.recordingId,
                      userId: instance.source.userId,
                      filename,
                    }))}
                    onShowScreenshots={onShowScreenshots}
                  />
                </div>
              </div>
            );
          })}

          {/* A surplus, like every other delta in the panel, coloured by whichever run spent the
              longer on this step. It needs both readings on screen to mean anything, so it waits
              until both columns are open. */}
          {detailsOpen.a && detailsOpen.b && durationDelta !== null && durationDelta !== 0 && (
            <div className="flex items-center justify-end gap-1 px-3 pb-3 text-[11px] tabular-nums">
              <span className="text-text-dim">{t("compare_delta")}</span>
              <span
                className={`font-medium ${compareSideTextClass(durationDelta > 0 ? "b" : "a")}`}
              >
                {`+${formatDuration(Math.abs(durationDelta))}`}
              </span>
            </div>
          )}
        </div>

        <WheelStack wheel={wheel} side="below" renderSlot={renderSlot} />
      </section>

      {/* Arrow-key tip — the wheel's main control, which nothing else on screen names. Sits below
          the wheel rather than over it, so it never covers a step's own content. */}
      {keysHintPhase !== "off" && (
        <div className="relative h-9">
          <StepKeysHint phase={keysHintPhase} variant="compare" />
        </div>
      )}
    </>
  );
};

/** Which pair is on the map, and the controls that re-aim it — the page header's compare face.
 *  It lives in the header rather than in the panel because the pair is the page's scope: it must
 *  stay readable and changeable however far the panel beside it has been scrolled, and whichever
 *  tab it is showing. Renders nothing while nothing is being compared. */
export const ProcessCompareHeaderControls = ({ state }: { state: ProcessGraphState }) => {
  const { t } = useTranslation();
  const instances = useComparedInstances(state);

  if (!instances) return null;

  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-text-dim">
        {t("comparing")}
      </span>
      {/* Left-to-right, the way the map colours the pair, with the swap between the two sides it
          exchanges. */}
      {state.comparison ? (
        <GroupSideLabel state={state} side="a" />
      ) : (
        <CompareSideRow state={state} side="a" instance={instances.a} compact />
      )}
      <button
        type="button"
        onClick={state.swapComparison}
        aria-label={t("compare_swap_sides")}
        title={t("compare_swap_sides")}
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-outline bg-surface text-text-dim transition hover:bg-surface-strong hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
      >
        <ArrowLeftRight className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      {state.comparison ? (
        <GroupSideLabel state={state} side="b" />
      ) : (
        <CompareSideRow state={state} side="b" instance={instances.b} compact />
      )}
    </div>
  );
};

const GroupSideLabel = ({ state, side }: { state: ProcessGraphState; side: CompareSide }) => {
  const { t } = useTranslation();
  const group = state.comparison?.[side];
  const identity = useCompareIdentity(group?.runs[0] ?? null);
  const singleRun = state.comparison?.mode === "runs";
  if (!group) return null;
  const label = singleRun ? (identity?.label ?? group.label) : group.label;
  return (
    <span
      className="flex h-8 min-w-0 max-w-[16rem] items-center gap-1.5 rounded-full border px-2 text-xs"
      style={{
        borderColor: compareSideColor(side),
        backgroundColor: `color-mix(in srgb, ${compareSideColor(side)} 18%, var(--color-surface))`,
      }}
      title={label}
    >
      <SideMark side={side} />
      <span className="truncate">{label}</span>
      <span className="shrink-0 text-text-dim">
        ({t("n_instances", { count: group.runs.length })})
      </span>
    </span>
  );
};

/** Familiar app lists, with one time baseline across both sides and shares within each side. */
const CompareAppUsage = ({
  instances,
  aLabel,
  bLabel,
  groups,
}: {
  groups?: { a: ProcessInstance[]; b: ProcessInstance[] };
  instances: { a: ProcessInstance; b: ProcessInstance };
  aLabel: string;
  bLabel: string;
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState({ a: false, b: false });
  const rows = useMemo(() => {
    const rowsFor = (side: CompareSide) => {
      const runs = groups?.[side] ?? [instances[side]];
      return buildPooledAppRows(
        runs.flatMap(run =>
          run.steps.map(step => ({
            ...step,
            duration: step.duration / runs.length,
            appSeconds: step.appSeconds
              ? Object.fromEntries(
                  Object.entries(step.appSeconds).map(([app, seconds]) => [
                    app,
                    seconds / runs.length,
                  ]),
                )
              : undefined,
          })),
        ),
        t("other_apps"),
      );
    };
    return { a: rowsFor("a"), b: rowsFor("b") };
  }, [instances, groups, t]);
  if (rows.a.length === 0 && rows.b.length === 0) return null;
  const maxSeconds = Math.max(0, ...[...rows.a, ...rows.b].map(row => row.seconds));

  return (
    <PanelSection
      title={t("app_usage")}
      description={t(
        groups ? "processPage.comparisonGroupAppHelp" : "processPage.comparisonAppHelp",
      )}
    >
      <div className="grid gap-5 @lg:grid-cols-2">
        {(["a", "b"] as const).map(side => (
          <div key={side}>
            <div className="mb-1.5 flex items-center gap-1.5">
              <SideMark side={side} />
              <span className={`min-w-0 text-sm font-medium ${compareSideTextClass(side)}`}>
                {side === "a" ? aLabel : bLabel}
              </span>
            </div>
            {rows[side].length > 0 ? (
              <AppUsageBars
                rows={expanded[side] ? rows[side] : rows[side].slice(0, 3)}
                barColor={`color-mix(in srgb, ${compareSideColor(side)} 75%, transparent)`}
                maxSeconds={maxSeconds}
              />
            ) : (
              <p className="text-xs text-text-dim">{t("no_data")}</p>
            )}
            {rows[side].length > 3 && (
              <div className="pt-2">
                <ShowAllButton
                  expanded={expanded[side]}
                  label={t("processPage.comparisonShowApps", { count: rows[side].length })}
                  onClick={() => setExpanded(current => ({ ...current, [side]: !current[side] }))}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </PanelSection>
  );
};

const ProcessComparePanel = ({
  state,
  onFocusStep,
  onReachBoundary,
  onShowScreenshots,
  onAskMetric,
  onAskWaitingTime,
  stepKeysActive,
  showStepKeysHint,
  onStepKeysHintShown,
}: {
  state: ProcessGraphState;
  /** The wheel moved: select the step it is resting on, recentre the map, and follow its run. */
  onFocusStep: (nodeId: string, focus?: CompareStepFocus) => void;
  /** The wheel was turned past either end: the map says so above the step it stayed on. */
  onReachBoundary: (nodeId: string, kind: "start" | "end") => void;
  onShowScreenshots: (screenshots: ScreenshotRef[]) => void;
  onAskMetric: (ask: CompareMetricAskContext) => void;
  /** A pause in one of the two runs, handed to the Ask tab the way the instance wheel hands it. */
  onAskWaitingTime: (transition: TransitionWaitAskContext) => void;
  /** False while a step inspector opened in instance mode owns the arrow keys instead. */
  stepKeysActive: boolean;
  /** True while the arrow-key tip is still owed for this visit to the map. */
  showStepKeysHint: boolean;
  /** Called as the tip appears, so it is offered only once per visit. */
  onStepKeysHintShown: () => void;
}) => {
  const { t } = useTranslation();
  const instances = useComparedInstances(state);
  const aIdentity = useCompareIdentity(instances?.a ?? null);
  const bIdentity = useCompareIdentity(instances?.b ?? null);
  const groups = state.comparisonData;
  const grouped = !!state.comparison && state.comparison.mode !== "runs";
  const stats = useMemo(
    () =>
      grouped && groups
        ? { a: compareGroupStats(groups.a), b: compareGroupStats(groups.b) }
        : instances
          ? {
              a: compareInstanceStats(instances.a),
              b: compareInstanceStats(instances.b),
            }
          : null,
    [instances, grouped, groups],
  );
  const metrics = useMemo(
    () => (stats ? buildMetrics(stats.a, stats.b, t, grouped) : []),
    [stats, t, grouped],
  );
  // Aligned once here for both readers: the wheel walks the rows, while the section header counts
  // how much of the two paths is actually the same step at the same point of the alignment — the
  // one number that says, before the wheel is turned at all, whether these runs mostly agree. It
  // rides on the section title so it stays readable with the wheel collapsed.
  const rows = useMemo(
    () => (instances ? alignInstanceSteps(instances.a.steps, instances.b.steps) : []),
    [instances],
  );
  const sharedCount = useMemo(() => rows.filter(row => row.kind === "shared").length, [rows]);

  // The sequence is the main reading for a case pair, so it is available immediately.
  const [stepFlowOpen, setStepFlowOpen] = useState(true);
  const selectedNodeId = state.selection?.kind === "node" ? state.selection.id : null;
  // The step compare was launched on is not a pick made inside compare mode: only a selection made
  // from here on counts as one.
  const openedOnNodeId = useRef(selectedNodeId);
  useEffect(() => {
    if (!selectedNodeId || selectedNodeId === openedOnNodeId.current) return;
    openedOnNodeId.current = selectedNodeId;
    setStepFlowOpen(true);
  }, [selectedNodeId]);

  if (!instances || !stats) return null;
  const aLabel = (grouped ? state.comparison?.a.label : aIdentity?.label) ?? instances.a.id;
  const bLabel = (grouped ? state.comparison?.b.label : bIdentity?.label) ?? instances.b.id;

  return (
    <div className="@container min-w-0">
      <div className="mb-4 grid grid-cols-2 gap-3">
        {(["a", "b"] as const).map(side => (
          <div
            key={side}
            className="min-w-0 rounded-xl border border-outline p-3"
            style={{
              backgroundColor: `color-mix(in srgb, ${compareSideColor(side)} 10%, var(--color-surface))`,
            }}
          >
            <div className="flex items-start gap-2">
              <SideMark side={side} className="mt-0.5" />
              <div className="min-w-0">
                <p className="break-words text-sm font-semibold text-text">
                  {grouped
                    ? state.comparison?.[side].label
                    : side === "a"
                      ? aIdentity?.name
                      : bIdentity?.name}
                </p>
                <p className="mt-1 text-xs text-text-mid">
                  {grouped
                    ? t("n_instances", { count: state.comparison?.[side].runs.length })
                    : side === "a"
                      ? aIdentity?.startTime
                      : bIdentity?.startTime}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
      <PanelSection
        title={t("processPage.comparisonSummary")}
        description={t(grouped ? "processPage.groupValues" : "processPage.comparisonCaseValues")}
        className="mt-5"
      >
        <CompareMetricsTable
          metrics={metrics}
          aLabel={aLabel}
          bLabel={bLabel}
          onAsk={onAskMetric}
        />
      </PanelSection>

      {grouped ? (
        <PanelSection
          title={t("processPage.stepDifferences")}
          description={t("processPage.groupStepsHelp")}
        >
          <GroupStepComparison
            key={`${state.comparePair?.aIds?.join(",")}:${state.comparePair?.bIds?.join(",")}`}
            state={state}
            onFocusStep={onFocusStep}
          />
        </PanelSection>
      ) : (
        <CollapsibleSidebarSection
          title={t("compare_step_flow")}
          headerIcon={<Columns2 className="h-4 w-4" aria-hidden="true" />}
          titleAccessory={`(${t("compare_shared_steps", { count: sharedCount })})`}
          open={stepFlowOpen}
          onOpenChange={setStepFlowOpen}
          separated={false}
          className="mt-4 border-t border-outline pt-3"
        >
          <CompareStepWheel
            key={`${instances.a.id}:${instances.b.id}`}
            state={state}
            instances={instances}
            rows={rows}
            onFocusStep={onFocusStep}
            onReachBoundary={onReachBoundary}
            onShowScreenshots={onShowScreenshots}
            onAskWaitingTime={onAskWaitingTime}
            stepKeysActive={stepKeysActive}
            showStepKeysHint={showStepKeysHint}
            onStepKeysHintShown={onStepKeysHintShown}
          />
        </CollapsibleSidebarSection>
      )}
      <CompareAppUsage
        instances={instances}
        groups={grouped && groups ? groups : undefined}
        aLabel={aLabel}
        bLabel={bLabel}
      />
    </div>
  );
};

const GroupStepComparison = ({
  state,
  onFocusStep,
}: { state: ProcessGraphState; onFocusStep: (nodeId: string) => void }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const pair = state.comparePair;
  const rows = useMemo(() => {
    if (!pair) return [];
    return state.graph.nodes
      .filter(node => node.id !== state.graph.start && node.id !== state.graph.end)
      .map(node => ({ node, stats: compareNodeStats(node, pair) }))
      .filter(({ stats }) => stats.a.count > 0 || stats.b.count > 0)
      .sort(
        (a, b) =>
          Math.abs(b.stats.b.seconds - b.stats.a.seconds) -
            Math.abs(a.stats.b.seconds - a.stats.a.seconds) ||
          Math.max(b.stats.a.seconds, b.stats.b.seconds) -
            Math.max(a.stats.a.seconds, a.stats.b.seconds) ||
          a.node.name.localeCompare(b.node.name),
      );
  }, [state.graph, pair]);
  if (!pair) return null;
  const maxSeconds = Math.max(
    0,
    ...rows.flatMap(row => [row.stats.a.seconds, row.stats.b.seconds]),
  );
  const selectedNodeId = state.selection?.kind === "node" ? state.selection.id : null;
  const columns =
    "grid grid-cols-[minmax(0,1fr)_5rem_5rem] gap-2 @lg:grid-cols-[minmax(0,1fr)_7.5rem_7.5rem]";
  return (
    <div>
      <div className={`${columns} mb-2 text-xs text-text-dim`}>
        <span>{t("processPage.activeTimePerRun")}</span>
        <span>
          <SideMark side="a" />
          <span className="sr-only">A</span>
        </span>
        <span>
          <SideMark side="b" />
          <span className="sr-only">B</span>
        </span>
      </div>
      <ul className="space-y-1">
        {(expanded ? rows : rows.slice(0, 5)).map(({ node, stats }) => {
          const difference = stats.b.seconds - stats.a.seconds;
          return (
            <li key={node.id}>
              <button
                type="button"
                aria-pressed={selectedNodeId === node.id}
                title={node.app ? `${node.name} (${node.app})` : node.name}
                onClick={() => onFocusStep(node.id)}
                className={`${columns} w-full items-center rounded-lg px-2 py-2.5 text-left text-sm transition hover:bg-surface-strong focus-visible:ring-2 focus-visible:ring-primary/30 ${selectedNodeId === node.id ? "bg-info/10" : ""}`}
              >
                <span className="min-w-0">
                  <span className="flex items-center gap-2">
                    <StepAppIcon app={node.app} />
                    <span className="min-w-0 truncate font-medium text-text">{node.name}</span>
                  </span>
                  <span className="mt-1 block text-xs text-text-mid">
                    {node.app && <span>{node.app} · </span>}
                    {t("processPage.comparisonDelta")}{" "}
                    {difference === 0
                      ? "0s"
                      : `${difference > 0 ? "+" : "−"}${formatDuration(Math.abs(difference))}`}
                  </span>
                </span>
                {(["a", "b"] as const).map(side => {
                  const ids = new Set(
                    side === "a" ? (pair.aIds ?? [pair.aId]) : (pair.bIds ?? [pair.bId]),
                  );
                  const cases = new Set(
                    node.instances
                      .filter(item => ids.has(item.instanceId))
                      .map(item => item.instanceId),
                  ).size;
                  const percent = (cases / ids.size) * 100;
                  return (
                    <span
                      key={side}
                      className="min-w-0"
                      title={t("processPage.comparisonStepCoverage", {
                        percent: percent > 0 && percent < 1 ? "<1" : Math.round(percent),
                        occurrences: formatCompareCount(stats[side].count),
                      })}
                    >
                      {stats[side].count > 0 ? (
                        <>
                          <span className={`block tabular-nums ${compareSideTextClass(side)}`}>
                            {formatDuration(stats[side].seconds)}
                          </span>
                          <span className="my-1 block h-1.5 overflow-hidden rounded-full bg-muted-surface">
                            <span
                              className="block h-full rounded-full"
                              style={{
                                width: `${maxSeconds > 0 ? (stats[side].seconds / maxSeconds) * 100 : 0}%`,
                                backgroundColor: compareSideColor(side),
                              }}
                            />
                          </span>
                          <span className="block text-[10px] tabular-nums text-text-mid">
                            {percent > 0 && percent < 1 ? "<1" : Math.round(percent)}% ·{" "}
                            {formatCompareCount(stats[side].count)}×
                          </span>
                        </>
                      ) : (
                        <span
                          aria-label={t("processPage.comparisonNoStep")}
                          className="text-text-dim"
                        >
                          —
                        </span>
                      )}
                    </span>
                  );
                })}
              </button>
            </li>
          );
        })}
      </ul>
      {rows.length > 5 && (
        <ShowAllButton
          expanded={expanded}
          label={t("processPanel.showAllSteps", { count: rows.length })}
          onClick={() => setExpanded(value => !value)}
        />
      )}
    </div>
  );
};

export default ProcessComparePanel;
